// ✅ src/pages/VehicleV2/DayWiseDailyProgressVehicleWiseReport_Print.jsx
// Template same like your sample print design
// ✅ Overlap FIX: table-layout:auto + min-width + column min widths (no more overlap)

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/dayWiseDailyProgressReportVehicleWise";

const toStr = (v) => String(v ?? "").trim();

export default function DayWiseDailyProgressVehicleWiseReport_Print() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // ✅ Read from navigation state
  const vehId = toStr(state?.vehId);
  const vehicleCode = toStr(state?.vehicleCode);
  const progDate = toStr(state?.progDate); // dd/MM/yyyy
  const progMonth = toStr(state?.progMonth);
  const progYear = toStr(state?.progYear);

  const [rows, setRows] = useState([]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrint = () => window.print();

  const get = (obj, ...keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  const fetchPrint = async () => {
    try {
      if ((!vehId && !vehicleCode) || !progDate || !progMonth || !progYear) return;

      setLoading(true);

      const params = { vehId, vehicleCode, progDate, progMonth, progYear };

      // ✅ If you don't have /print API yet, change to `${API_BASE}/list`
      const { data } = await axios.get(`${API_BASE}/print`, { params });

      if (!data?.success) throw new Error(data?.message || "Failed to load print data");

      setPayload(data);
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      alert(err?.message || "Failed to load print data");
      setRows([]);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line
  }, []);

  const now = new Date();
  const nowStr = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

  // optional payload info
  const monthName = payload?.month || payload?.monthName || progMonth || "";
  const yearName = payload?.year || progYear || "";

  return (
    <div className="min-h-screen bg-white p-3">
      {/* Buttons hidden while printing */}
      <div className="print:hidden mb-4 flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>

        <button
          onClick={handlePrint}
          disabled={loading}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800"
        >
          {loading ? "Loading..." : "Print"}
        </button>
      </div>

      <div className="mx-auto border-2 border-black p-2 print:mx-0 print:max-w-none">
        <div className="border border-black p-2">
          {/* Header */}
          <div className="border border-black p-2 mb-2 flex justify-between items-center">
            <div className="text-xs font-bold">
              NISSI INFOTECH PVT LTD
              <div className="text-[10px] font-normal">CIVIL CONSTRUCTION</div>
            </div>
            <div className="text-[10px]">{nowStr}</div>
          </div>

          <div className="text-center font-bold text-sm my-2">
            DAY WISE DAILY PROGRESS VEHICLE WISE REPORT ({progDate || ""})
          </div>

          {/* Meta line */}
          <div className="border border-black p-2 mb-2 text-[10px] flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <b>Vehicle Code:</b> {vehicleCode || "-"}
            </div>
            <div>
              <b>Vehicle Id:</b> {vehId || "-"}
            </div>
            <div>
              <b>Month:</b> {monthName || "-"}
            </div>
            <div>
              <b>Year:</b> {yearName || "-"}
            </div>
            <div>
              <b>Progress Date:</b> {progDate || "-"}
            </div>
          </div>

          {!rows.length ? (
            <div className="text-center p-6 border border-black text-xs">
              No Records Found
            </div>
          ) : (
            <div className="print-wrap">
              <table className="print-table">
                <thead>
                  <tr>
                    <th className="th w-sno">S.No</th>
                    <th className="th w-branch">Branch</th>
                    <th className="th w-date">Progress Date</th>
                    <th className="th w-status">Status</th>
                    <th className="th w-remarks">Remarks</th>
                    <th className="th w-loc">Work Location</th>

                    <th className="th w-small">Total Trips</th>
                    <th className="th w-med">Trips Location Wise</th>
                    <th className="th w-med">From</th>
                    <th className="th w-med">To</th>

                    <th className="th w-small">Trip Open</th>
                    <th className="th w-small">Trip Close</th>
                    <th className="th w-small">Trip Total</th>

                    <th className="th w-big">Work Desc</th>
                    <th className="th w-small">Load</th>
                    <th className="th w-small">UOM</th>
                    <th className="th w-med">Driver Name</th>

                    <th className="th w-small">Start KM</th>
                    <th className="th w-small">Close KM</th>
                    <th className="th w-small">Work KM</th>

                    <th className="th w-small">Start HR</th>
                    <th className="th w-small">Close HR</th>
                    <th className="th w-small">Work HR</th>

                    <th className="th w-small">Load Time</th>
                    <th className="th w-small">UnLoad</th>
                    <th className="th w-small">Tot Time</th>

                    <th className="th w-small">Tank Open</th>
                    <th className="th w-small">Diesel Qty</th>
                    <th className="th w-small">Diesel Amt</th>
                    <th className="th w-small">Fuel Cons</th>

                    <th className="th w-small">Prod Qty</th>
                    <th className="th w-small">Tank Close</th>
                    <th className="th w-small">Tank Cap</th>

                    <th className="th w-small">Diesel/KM</th>
                    <th className="th w-small">Diesel/HR</th>
                    <th className="th w-small">Cost/KM</th>
                    <th className="th w-small">Cost/HR</th>
                    <th className="th w-small">Avg Alert</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="td center w-sno">{get(r, "SNo") || idx + 1}</td>
                      <td className="td w-branch">{get(r, "fld_branchname")}</td>
                      <td className="td center w-date">{get(r, "fld_progressdate")}</td>
                      <td className="td w-status">{get(r, "fld_status")}</td>
                      <td className="td w-remarks">{get(r, "fld_remarks")}</td>
                      <td className="td w-loc">{get(r, "fld_worklocationname")}</td>

                      <td className="td center w-small">{get(r, "fld_nooftrips")}</td>
                      <td className="td center w-med">{get(r, "fld_tripslocationwise")}</td>
                      <td className="td w-med">{get(r, "fld_fromLocation", "fld_Fromlocation")}</td>
                      <td className="td w-med">{get(r, "fld_ToLocation", "fld_Tolocation")}</td>

                      <td className="td center w-small">{get(r, "fld_tripopening")}</td>
                      <td className="td center w-small">{get(r, "fld_tripclosing")}</td>
                      <td className="td center w-small">{get(r, "fld_triptotal")}</td>

                      <td className="td w-big">{get(r, "fld_descripttion", "fld_Descripttion")}</td>
                      <td className="td center w-small">{get(r, "fld_noofloads")}</td>
                      <td className="td center w-small">{get(r, "fld_uomname")}</td>
                      <td className="td w-med">{get(r, "fld_drivername")}</td>

                      <td className="td center w-small">{get(r, "fld_startingkilometer")}</td>
                      <td className="td center w-small">{get(r, "fld_closingkilometer")}</td>
                      <td className="td center w-small">{get(r, "fld_workingkm")}</td>

                      <td className="td center w-small">{get(r, "fld_statrtinghrs")}</td>
                      <td className="td center w-small">{get(r, "fld_closinghrs")}</td>
                      <td className="td center w-small">{get(r, "fld_workinghrs")}</td>

                      <td className="td center w-small">{get(r, "fld_loadtime")}</td>
                      <td className="td center w-small">{get(r, "fld_unloadtime")}</td>
                      <td className="td center w-small">{get(r, "fld_totaltime")}</td>

                      <td className="td center w-small">{get(r, "fld_fueltank_opening")}</td>
                      <td className="td center w-small">{get(r, "fld_dieselquantity")}</td>
                      <td className="td center w-small">{get(r, "fld_dieselamount")}</td>
                      <td className="td center w-small">{get(r, "fld_fuelconsumption")}</td>

                      <td className="td center w-small">{get(r, "fld_productionquantity")}</td>
                      <td className="td center w-small">{get(r, "fld_fueltank_closing")}</td>
                      <td className="td center w-small">{get(r, "fld_vehfueltankcapacity")}</td>

                      <td className="td center w-small">{get(r, "fld_dieselconsumptionkm")}</td>
                      <td className="td center w-small">{get(r, "fld_dieselconsumptionHrs")}</td>
                      <td className="td center w-small">{get(r, "fld_dieselcostkm")}</td>
                      <td className="td center w-small">{get(r, "fld_dieselcosthr")}</td>
                      <td className="td center w-small">{get(r, "fld_averagestatus")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer Sign */}
          <div className="mt-10 flex justify-between text-xs px-4">
            <div>Prepared By</div>
            <div>Checked By</div>
            <div>Approved By</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print-wrap {
            overflow: visible !important;
          }
        }

        .print-wrap {
          overflow-x: auto;
        }

        /* ✅ FIX: no overlap */
        .print-table {
          border-collapse: collapse;
          width: 100%;
          table-layout: auto;
          min-width: 2400px;
        }

        .th,
        .td {
          border: 1px solid #000;
          padding: 2px 4px;
          font-size: 9px;
          line-height: 1.2;
          white-space: nowrap;
          vertical-align: middle;
        }

        .th {
          font-weight: 700;
          text-align: center;
          background: #f2f2f2;
        }

        .center {
          text-align: center;
        }

        /* ✅ column widths */
        .w-sno { min-width: 45px; }
        .w-branch { min-width: 90px; }
        .w-date { min-width: 95px; }
        .w-status { min-width: 80px; }
        .w-remarks { min-width: 120px; }
        .w-loc { min-width: 140px; }

        .w-small { min-width: 70px; }
        .w-med { min-width: 95px; }
        .w-big { min-width: 160px; }
      `}</style>
    </div>
  );
}
